import { useContext, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import * as XLSX from 'xlsx'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'
import AddIcon from '@mui/icons-material/Add'
import SendIcon from '@mui/icons-material/Send'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { exportToExcel } from '../../../utils/export'
import { createBulkMachineReceipt } from '../../../services/machineRecipeServices'
import { mensagemDeErro } from '../../../services/apiClient'
import PermissionComponent from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'

const BATCH_SIZE = 500

const EMPTY_FORM = { sequenceReceipt: '', description: '', internalPartNumber: '' }

const TEMPLATE_COLUMNS = [
  { header: 'Sequência do Recibo', accessor: 'sequenceReceipt' },
  { header: 'Descrição', accessor: 'description' },
  { header: 'Número interno', accessor: 'internalPartNumber' },
]

/** Cabeçalhos aceitos por campo, já normalizados (sem acento, só letras/números). */
const FIELD_ALIASES = {
  sequenceReceipt: [
    'sequencereceipt',
    'sequenciadorecibo',
    'codigodareceita',
    'codigoreceita',
    'sequencia',
  ],
  description: ['description', 'descricao'],
  internalPartNumber: [
    'internalparnumber',
    'internalpartnumber',
    'numerointerno',
    'numerointernodapeca',
    'numerodapeca',
  ],
}

/** NFD separa os acentos e o filtro final descarta tudo que não é letra/número. */
const normalizeHeader = value =>
  String(value === null || value === undefined ? '' : value)
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const pickField = (sheetRow, field) => {
  const key = Object.keys(sheetRow).find(name =>
    FIELD_ALIASES[field].includes(normalizeHeader(name))
  )
  if (!key) return ''
  const value = sheetRow[key]
  return value === null || value === undefined ? '' : String(value).trim()
}

const chunk = (list, size) => {
  const parts = []
  for (let index = 0; index < list.length; index += size) {
    parts.push(list.slice(index, index + size))
  }
  return parts
}

const StatusChip = ({ item }) => {
  if (item.status === 'sending') {
    return (
      <Chip
        size="small"
        color="default"
        icon={<CircularProgress size={12} sx={{ ml: 1 }} />}
        label="Enviando..."
      />
    )
  }
  if (item.status === 'sent') return <Chip size="small" color="success" label="Enviado" />
  if (item.status === 'error') {
    return (
      <Tooltip title={item.error}>
        <Chip size="small" color="error" label={item.error} sx={{ maxWidth: 260 }} />
      </Tooltip>
    )
  }
  return <Chip size="small" color="info" label="Pronto para enviar" />
}

const ChargeRecipeMachine = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()

  const nextId = useRef(0)
  const fileInputRef = useRef(null)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [items, setItems] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [form, setForm] = useState(EMPTY_FORM)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [pendingSend, setPendingSend] = useState(null)
  const [clearOpen, setClearOpen] = useState(false)

  const counters = useMemo(
    () => ({
      total: items.length,
      selected: selectedIds.length,
      sent: items.filter(item => item.status === 'sent').length,
      error: items.filter(item => item.status === 'error').length,
    }),
    [items, selectedIds]
  )

  const pagedItems = useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  )

  const addItems = newItems => {
    const withId = newItems.map(item => {
      nextId.current += 1
      return { ...item, id: nextId.current, status: 'pending', error: '' }
    })
    setItems(current => [...current, ...withId])
    setSelectedIds(current => [...current, ...withId.map(item => item.id)])
  }

  const handleFile = async event => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    if (!file) return

    try {
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      if (!sheet) {
        notify('A planilha não possui abas legíveis.', 'error')
        return
      }

      const sheetRows = XLSX.utils.sheet_to_json(sheet, { defval: '' })
      const parsed = sheetRows.map(sheetRow => ({
        sequenceReceipt: pickField(sheetRow, 'sequenceReceipt'),
        description: pickField(sheetRow, 'description'),
        internalPartNumber: pickField(sheetRow, 'internalPartNumber'),
      }))

      const valid = parsed.filter(item => item.sequenceReceipt)
      const invalid = parsed.length - valid.length

      if (valid.length === 0) {
        notify(
          'Nenhuma linha válida encontrada. Confira os cabeçalhos usando o modelo disponível.',
          'warning'
        )
        return
      }

      addItems(valid)
      setPage(0)
      notify(
        invalid > 0
          ? `${valid.length} linha(s) carregada(s), ${invalid} descartada(s) sem sequência do recibo.`
          : `${valid.length} linha(s) carregada(s).`,
        invalid > 0 ? 'warning' : 'success'
      )
    } catch (error) {
      notify('Não foi possível ler a planilha selecionada.', 'error')
    }
  }

  const downloadTemplate = () => {
    const example = [
      {
        sequenceReceipt: 'REC-0001',
        description: 'Receita de exemplo',
        internalPartNumber: 'PN-0001',
      },
    ]
    exportToExcel(example, TEMPLATE_COLUMNS, 'modelo-carga-receita-maquina', 'Modelo')
  }

  const handleAddManual = () => {
    if (!form.sequenceReceipt.trim()) {
      notify('Informe a sequência do recibo.', 'warning')
      return
    }
    addItems([
      {
        sequenceReceipt: form.sequenceReceipt.trim(),
        description: form.description.trim(),
        internalPartNumber: form.internalPartNumber.trim(),
      },
    ])
    setForm(EMPTY_FORM)
  }

  const toggleItem = id =>
    setSelectedIds(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    )

  const toggleAll = () =>
    setSelectedIds(current => (current.length === items.length ? [] : items.map(item => item.id)))

  const removeItem = id => {
    setItems(current => current.filter(item => item.id !== id))
    setSelectedIds(current => current.filter(item => item !== id))
  }

  const clearList = () => {
    setItems([])
    setSelectedIds([])
    setPage(0)
    setProgress({ done: 0, total: 0 })
    setClearOpen(false)
  }

  const setBatchStatus = (batchIds, status, error = '') => {
    const idSet = new Set(batchIds)
    setItems(current =>
      current.map(item => (idSet.has(item.id) ? { ...item, status, error } : item))
    )
  }

  /**
   * O lote responde 200 mesmo com itens recusados — por contrato ele não aborta
   * no primeiro erro, devolve `failed` com o motivo de cada um. Marcar o lote
   * inteiro como enviado só porque o HTTP deu certo esconderia exatamente o que
   * o operador precisa corrigir e reenviar.
   */
  const applyBatchOutcome = (batchIds, motivoPorSequencia) => {
    const idSet = new Set(batchIds)
    setItems(current =>
      current.map(item => {
        if (!idSet.has(item.id)) return item
        const motivo = motivoPorSequencia.get(item.sequenceReceipt)
        return motivo
          ? { ...item, status: 'error', error: motivo }
          : { ...item, status: 'sent', error: '' }
      })
    )
  }

  const runSend = async targets => {
    setSending(true)
    setProgress({ done: 0, total: targets.length })

    let done = 0
    let failed = 0

    for (const batch of chunk(targets, BATCH_SIZE)) {
      const batchIds = batch.map(item => item.id)
      setBatchStatus(batchIds, 'sending')

      try {
        const response = await createBulkMachineReceipt(
          batch.map(item => ({
            sequenceReceipt: item.sequenceReceipt,
            description: item.description,
            internalPartNumber: item.internalPartNumber,
            subCompanyId,
          }))
        )
        const recusados = response.data?.failed || []
        failed += recusados.length
        applyBatchOutcome(
          batchIds,
          new Map(recusados.map(recusa => [recusa.item?.sequenceReceipt, recusa.motivo]))
        )
      } catch (error) {
        failed += batch.length
        setBatchStatus(batchIds, 'error', mensagemDeErro(error, 'Falha ao enviar o lote.'))
      }

      done += batch.length
      setProgress({ done, total: targets.length })
    }

    setSending(false)
    if (failed === 0) {
      notify(`${targets.length} receita(s) enviada(s) com sucesso!`, 'success')
    } else {
      notify(`Envio concluído com ${failed} item(ns) em erro.`, 'warning')
    }
  }

  const requestSend = scope => {
    if (!subCompanyId) {
      notify('Selecione a filial antes de enviar.', 'warning')
      return
    }
    const targets =
      scope === 'selected' ? items.filter(item => selectedIds.includes(item.id)) : items
    if (targets.length === 0) {
      notify('Nenhum item para enviar.', 'warning')
      return
    }
    setPendingSend(targets)
  }

  const confirmSend = () => {
    const targets = pendingSend
    setPendingSend(null)
    runSend(targets)
  }

  const allSelected = items.length > 0 && selectedIds.length === items.length

  const columns = [
    {
      id: 'select',
      width: 60,
      label: (
        <Checkbox
          size="small"
          sx={{ color: 'common.white', '&.Mui-checked': { color: 'common.white' } }}
          checked={allSelected}
          indeterminate={selectedIds.length > 0 && !allSelected}
          disabled={items.length === 0 || sending}
          onChange={toggleAll}
        />
      ),
      element: row => (
        <Checkbox
          size="small"
          checked={selectedIds.includes(row.id)}
          disabled={sending}
          onChange={() => toggleItem(row.id)}
        />
      ),
    },
    {
      id: 'sequenceReceipt',
      label: 'Sequência do Recibo',
      element: row => row.sequenceReceipt || '—',
    },
    { id: 'description', label: 'Descrição', element: row => row.description || '—' },
    {
      id: 'internalPartNumber',
      label: 'Número Interno',
      element: row => row.internalPartNumber || '—',
    },
    { id: 'status', label: 'Status', element: row => <StatusChip item={row} /> },
    {
      id: 'actions',
      label: 'Ações',
      width: 80,
      element: row => (
        <Tooltip title="Remover da lista">
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={sending}
              onClick={() => removeItem(row.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ]

  const progressPercent =
    progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)

  return (
    <Box>
      <PageHeader
        title="Carga de Receita de Máquina"
        subtitle="Importe receitas em lote a partir de uma planilha"
        actions={
          <>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
              Baixar modelo
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              disabled={items.length === 0 || sending}
              onClick={() => setClearOpen(true)}
            >
              Limpar lista
            </Button>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
              label="Filial para carga"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Importar planilha
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Formatos aceitos: .xls, .xlsx e .csv. A primeira aba é lida e as colunas reconhecidas são
          Sequência do Recibo, Descrição e Número interno.
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          hidden
          onChange={handleFile}
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          disabled={sending}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          Selecionar planilha
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Adicionar manualmente
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Sequência do Recibo"
              value={form.sequenceReceipt}
              onChange={event => setForm({ ...form, sequenceReceipt: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Descrição"
              value={form.description}
              onChange={event => setForm({ ...form, description: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Número interno"
              value={form.internalPartNumber}
              onChange={event => setForm({ ...form, internalPartNumber: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={sending}
              onClick={handleAddManual}
            >
              Adicionar à lista
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Total: ${counters.total}`} />
          <Chip label={`Selecionados: ${counters.selected}`} color="primary" variant="outlined" />
          <Chip label={`Enviados: ${counters.sent}`} color="success" variant="outlined" />
          <Chip label={`Com erro: ${counters.error}`} color="error" variant="outlined" />
        </Box>
        <PermissionComponent permissao={PERMISSOES.RECEITA_GERENCIAR}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              disabled={sending || counters.selected === 0}
              onClick={() => requestSend('selected')}
            >
              Enviar selecionados
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={sending || counters.total === 0}
              onClick={() => requestSend('all')}
            >
              Enviar lista
            </Button>
          </Box>
        </PermissionComponent>
      </Box>

      {sending && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={progressPercent} />
          <Typography variant="caption" color="text.secondary">
            {`${progress.done} de ${progress.total} enviados (${progressPercent}%)`}
          </Typography>
        </Box>
      )}

      <DataTable
        columns={columns}
        rows={pagedItems}
        page={page}
        rowsPerPage={rowsPerPage}
        total={items.length}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
        rowKey={row => row.id}
        emptyMessage="Nenhum item na lista. Importe uma planilha ou adicione manualmente."
      />

      <ConfirmDialog
        open={Boolean(pendingSend)}
        title="Enviar receitas de máquina"
        description={
          pendingSend
            ? `Confirma o envio de ${pendingSend.length} item(ns) em lotes de ${BATCH_SIZE}?`
            : ''
        }
        confirmLabel="Enviar"
        confirmColor="primary"
        onConfirm={confirmSend}
        onClose={() => setPendingSend(null)}
      />

      <ConfirmDialog
        open={clearOpen}
        title="Limpar lista"
        description="Tem certeza que deseja remover todos os itens da lista?"
        confirmLabel="Limpar"
        onConfirm={clearList}
        onClose={() => setClearOpen(false)}
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default ChargeRecipeMachine
